import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { endSession, updateUserProfile, searchPlaces } from '../api';
import InsufficientCreditsModal from './InsufficientCreditsModal';
import { logoutUser } from '../utils/logout';
import BirthDetailsModal from './utils/BirthDetailsModal';
import {
  COUNTRIES,
  RELIGIONS,
  MONTHS
} from '../constants/chatConstants';


const ALL_NAV_ITEMS = [
  { path: '/home', label: 'Home' },
  { path: '/chat', label: 'Chat' },
  { path: '/call', label: 'Appointment' },
  // { path: '/reports', label: 'Reports' },
  { path: '/refer', label: 'Refer' },
  { path: '/feedback', label: 'Feedback' },
  { path: '/aboutus', label: 'About Us' },
  { path: '/contactus', label: 'Contact Us' },
  { path: '/terms', label: 'Terms' }
];


const PUBLIC_NAV_ITEMS = [
  { path: '/home', label: 'Home' },
  { path: '/aboutus', label: 'About Us' },
  { path: '/contactus', label: 'Contact Us' },
  { path: '/terms', label: 'Terms' }
];



function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);


  const navItems = user ? ALL_NAV_ITEMS : PUBLIC_NAV_ITEMS;


  // Listen for storage changes (logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'astroguru_user' && !e.newValue) window.location.reload();
    };
    const handleUserLoggedOut = () => {
      setShowUserMenu(false);
      window.location.href = '/';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
  }, []);


  // Detect mobile app and apply safe area
  useEffect(() => {
    const isMobileApp = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      window.ReactNativeWebView ||
      /wv|WebView/i.test(navigator.userAgent || navigator.vendor || window.opera);


    if (isMobileApp()) document.documentElement.classList.add('mobile-app-mode');
  }, []);


  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container')) setShowUserMenu(false);
      if (!event.target.closest('.nav-dropdown-mobile')) {
        setShowNavMenu(false);
        document.body.classList.remove('mobile-menu-open');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // Logout handler using external function
  const handleLogout = useCallback(() => {
    logoutUser({
      user,
      logout,
      endSession,
      setIsLoggingOut,
      setShowUserMenu
    });
  }, [logout, user]);


  // Close mobile nav and remove body class
  const handleMobileMenuClick = () => {
    setShowNavMenu(false);
    document.body.classList.remove('mobile-menu-open');
  };



  const handleProfileClick = () => {
    setShowProfileEditModal(true);
    setShowUserMenu(false);
  };




  return (
    <>
      {isLoggingOut && (
        <div className="logout-overlay">
          <div className="logout-message">
            <div className="logout-spinner"></div>
            <p>Logging out securely...</p>
          </div>
        </div>
      )}


      {showInsufficientCreditsModal && (
        <InsufficientCreditsModal
          onClose={() => setShowInsufficientCreditsModal(false)}
          onPurchase={() => {
            setShowInsufficientCreditsModal(false);
            window.location.href = '/credits';
          }}
        />
      )}


      <header className="app-header">
        {/* Header Left: Logo */}
        <div className="header-left">
          <Link to="/home" className="logo-section">
            <span className="logo"></span>
            <h1 className="logo-text">AastroG</h1>
          </Link>
        </div>


        {/* Centered Navigation */}
        <div className="nav-container-center">
          {/* Desktop Horizontal Grid */}
          <div className="nav-grid-desktop">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-grid-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="nav-label">{item.label}</span>
              </Link>
            ))}
          </div>


          {/* Mobile: Dropdown Navigation */}
          <div className="nav-dropdown-mobile">
            <button
              className="nav-toggle"
              onClick={() => {
                setShowNavMenu((prev) => !prev);
                document.body.classList.toggle('mobile-menu-open');
              }}
            >
              <span className="current-page">☰</span>
            </button>
            {showNavMenu && (
              <div className="nav-menu show">
                <Link to="/home" className="menu-logo-section" onClick={handleMobileMenuClick}>
                  <span className="logo"></span>
                  <h1 className="logo-text">AastroG</h1>
                </Link>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={handleMobileMenuClick}
                  >
                    <span className="nav-label">{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>


        {/* Mobile center logo */}
        <div className="mobile-center-logo">
          <Link to="/home" className="center-logo-link">
            <span className="logo"></span>
            <h1 className="logo-text">AastroG</h1>
          </Link>
        </div>


        {/* Header Right: User Menu Only */}
        <div className="header-right">
          {user && (
              <div className="user-menu-container">
                <div
                  className="user-info"
                  onClick={() => setShowUserMenu((prev) => !prev)}
                >
                  <div className="user-avatar">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt="Profile"
                        className="avatar-img"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="avatar-initials">
                        {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <span className="user-name">
                    {user.full_name?.split(' ')[0] || 'User'}
                  </span>
                </div>
                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {user.profile_picture ? (
                          <img
                            src={user.profile_picture}
                            alt="Profile"
                            className="dropdown-avatar-img"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="dropdown-avatar-placeholder">
                            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-user-name">
                          {user.full_name || 'User'}
                        </div>
                        <div className="dropdown-user-email">
                          {user.email || 'user@example.com'}
                        </div>
                      </div>
                    </div>

                    {/* ✅ Credits Display Inside Dropdown */}
                    <Link to="/credits" className="dropdown-item dropdown-credits-item" onClick={() => setShowUserMenu(false)}>
                      <span className="dropdown-icon dimond">💎</span>
                      <span className="dropdown-credits-info">
                        <span className="dropdown-credits-label">Credits: </span>
                        <span className={`dropdown-credits-count ${user.credits < 10 ? 'low-credits' : ''}`}>
                          {user.credits || 0}
                        </span>
                      </span>
                      {user.credits < 10 && <span className="warning-icon">⚠️</span>}
                    </Link>

                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-item profile-modal-trigger"
                      onClick={handleProfileClick}
                    >
                      <span className="dropdown-icon">👤</span>
                      Profile
                    </button>
                    <div className="dropdown-divider"></div>
                    <button
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                    >
                      <span className="dropdown-icon">
                        {isLoggingOut ? '⏳' : '🚪'}
                      </span>
                      {isLoggingOut ? 'Logging out...' : 'Logout'}
                    </button>
                  </div>
                )}
              </div>
          )}
        </div>
      </header>


{showProfileEditModal && (
  <BirthDetailsModal
    show={showProfileEditModal}
    close={() => setShowProfileEditModal(false)}
    isEdit={true}
    isFromAuth={false}
    birthDetails={{
      full_name: user?.full_name || '',
      religion: user?.religion || 'Hindu',
      birth_day: user?.birth_date ? new Date(user.birth_date).getDate().toString() : '',
      birth_month: user?.birth_date ? (new Date(user.birth_date).getMonth() + 1).toString() : '',
      birth_year: user?.birth_date ? new Date(user.birth_date).getFullYear().toString() : '',
      birth_time: user?.birth_time || '',
      birth_place: user?.birth_place || '',
      country_code_no: user?.country_code_no || '+91',
      mobile_number: user?.mobile_number || '',
      country_code: user?.country_code || 'IN'
    }}
    COUNTRIES={COUNTRIES}
    RELIGIONS={RELIGIONS}
    MONTHS={MONTHS}
  />
)}
    </>
  );
}


export default Header;
